/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import StateComponent from './StateComponent';

/** Manager for handling multiple states and the completion status. */
export default class StateCompletionManager
{
    /** Dependency map to track completion per state. */
    private _stateDependencies: Map<StateComponent, boolean> = new Map();

    /** @hidden */
    constructor(
        stateComponents: StateComponent[]
    )
    {
        stateComponents.forEach(stateComponent => this._stateDependencies.set(stateComponent, false), this);
    }

    /**
     * Checks if completion for specific state is true.
     * @param state State to check against.
     * @returns True if dependency completed for state.
     */
    public IsStateCompleted(state: StateComponent): boolean
    {
        return this._stateDependencies.get(state) !== false;
    }

    /**
     * Used to mark complete status for specific state.
     * @param state State for which to mark completion.
     * @param toValue Value to set to.
     * @returns This [[StateCompletionManager]].
     */
    public SetStateCompleted(state: StateComponent, toValue: boolean = true): StateCompletionManager
    {
        if (!this._stateDependencies.has(state))
        {
            CLog.error(`Trying to set state [${state}] which is non-existant in completion manager.`,
                            state, LogTypes.StateManager);
        }

        this._stateDependencies.set(state, toValue);

        return this;
    }

    /**
     * Checks if dependencies contain [[StateComponent]].
     * @param state State to check if contained in dependencies.
     * @return True if contained in dependencies.
     */
    public Has(state: StateComponent): boolean
    {
        return this._stateDependencies.has(state);
    }

    /**
     * Sets each value in the completion manager false.
     */
    public Reset(): StateCompletionManager
    {
        this._stateDependencies.forEach(
            (isDependency, stateID, map) =>
            {
                map.set(stateID, false);
            });

        return this;
    }
}
